
import axios, {CancelTokenSource} from "axios";
import {Component, Vue} from "vue-property-decorator";
import {sunucuExcelKaydet} from "@/utils/sunucuExcelIndirme";

/**
 * DEV-3350 — kullanıcının Excel çıktıları.
 *
 * NEDEN VAR
 * Üretim arka planda sürüyor; kullanıcı düğmeye basıp sayfadan ayrılırsa iş
 * çalışmaya devam ediyor ama dosyaya ulaşacak bir yol yoktu — iş anahtarı
 * yalnız o sekmenin belleğindeydi. Dosya sunucuda üretilip saklama süresi
 * dolunca siliniyordu; kullanıcı beklediği çıktıyı hiç göremiyordu.
 *
 * Liste sunucu tarafında süzülür: herkes KENDİ çıktılarını görür, büro
 * yöneticisi büronun tümünü. Rapor çıktısı borçlu kimlik/adres/tutar taşıdığı
 * için sıradan bir kullanıcı başkasının çıktısını göremez; yönetici görünümünde
 * "İSTEYEN" kolonu hangi çıktının kime ait olduğunu söyler.
 *
 * ⚠ Kiracı süzmesi her hâlde geçerli: yönetici bile başka büronun çıktısını
 * göremez.
 */
interface ExcelIsi {
  is_key: string;
  isim: string;
  durum: string;
  satir?: number;
  hata?: string;
  istendi?: string;
  /** DEV-3422: rapor tavanı aştıysa dosya eksiktir; sessiz kalmamalı. */
  kesildi?: boolean;
  satir_limiti?: number;
  /**
   * Yönetici görünümünde dolu gelir: çıktıyı kim istedi.
   * Sunucu yalnız yöneticiye gönderir; normal kullanıcı zaten kendi
   * çıktılarını görüyor, kolonun bir anlamı olmaz.
   */
  sahibi?: string;
  benim_mi?: boolean;
}

@Component
export default class RaporExcelCiktilari extends Vue {
  isler: Array<ExcelIsi> = [];
  yukleniyor = false;
  private zamanlayici: any = null;
  private yenilemeBaslangici = 0;
  private aktifListeIstegi: CancelTokenSource | null = null;
  private aktifIndirmeIstekleri: { [isKey: string]: CancelTokenSource } = {};
  private yokEdildi = false;
  /**
   * Aynı kesinti boyunca liste hata mesajı yalnız bir kez gösterilir; liste 10 sn'de
   * bir tazelendiği için ağ kesintisinde her turda toast basmak ekranı doldururdu.
   * Bir sonraki başarılı yüklemede ve kullanıcı elle "Yenile"ye bastığında sıfırlanır.
   */
  private listeHatasiGosterildi = false;
  /** Aynı büyük Blob'un çift tıklamayla birden çok kez indirilmesini engeller. */
  indirilenIsler: { [isKey: string]: boolean } = {};
  /** Sunucudan gelir; ekranda ayrı bir sabit tutulmaz (ikisi ayrışmasın). */
  saklamaSaat = 24;

  mounted() {
    this.yenilemeBaslangici = Date.now();
    this.yukle();
  }

  beforeDestroy() {
    this.yokEdildi = true;
    this.yenilemeyiDurdur();
    if (this.aktifListeIstegi) {
      this.aktifListeIstegi.cancel("Excel çıktıları ekranından ayrıldı.");
      this.aktifListeIstegi = null;
    }
    Object.keys(this.aktifIndirmeIstekleri).forEach((isKey) => {
      this.aktifIndirmeIstekleri[isKey].cancel("Excel indirme ekranından ayrıldı.");
    });
    this.aktifIndirmeIstekleri = {};
  }

  private yenilemeyiDurdur() {
    if (this.zamanlayici) {
      clearTimeout(this.zamanlayici);
      this.zamanlayici = null;
    }
  }

  /** Bitmemiş iş var mı — otomatik tazelemenin sürüp sürmeyeceğini belirler. */
  get bekleyenVar(): boolean {
    return this.isler.some((i) => i.durum === "kuyrukta" || i.durum === "hazirlaniyor");
  }

  /**
   * Bitmemiş iş varken listeyi kendiliğinden tazeler.
   *
   * Üretim dakikalar sürebiliyor; kullanıcının "Yenile"ye basıp durması
   * beklenemez. Tavan 30 dakika: sunucudaki takılan-iş eşiğiyle aynı, o eşikte
   * iş zaten hataya çekiliyor. Tavan olmadan sekme açık kaldığı sürece istek
   * atmaya devam ederdi.
   */
  private tazelemeyiPlanla() {
    this.yenilemeyiDurdur();

    if (this.yokEdildi) return;
    if (!this.bekleyenVar) return;
    if (Date.now() - this.yenilemeBaslangici >= 30 * 60 * 1000) return;

    this.zamanlayici = setTimeout(() => {
      this.zamanlayici = null;
      this.yukle();
    }, 10000);
  }

  /**
   * Kullanıcı yenilerse otomatik tazeleme penceresi baştan başlar.
   *
   * Elle basışın sonucu her zaman bildirilir: kesinti sürerken "Yenile"ye basan
   * kullanıcı mesajsız kalırsa butonun hiç çalışmadığını sanar. Bu yüzden "bir kez"
   * kilidi burada açılır; otomatik yoklama yine kesinti başına bir kez mesaj verir.
   */
  elleYenile() {
    if (this.yukleniyor || this.yokEdildi) return;
    this.yenilemeyiDurdur();
    this.yenilemeBaslangici = Date.now();
    this.listeHatasiGosterildi = false;
    this.yukle();
  }

  yukle(): Promise<void> {
    if (this.yokEdildi || this.yukleniyor) return Promise.resolve();

    this.yukleniyor = true;
    const istek = axios.CancelToken.source();
    this.aktifListeIstegi = istek;

    return this.$http.get("/api/v1/rapor-ai/excel-isleri", {cancelToken: istek.token})
        .then((cevap: any) => {
          if (this.yokEdildi) return;
          const veri = cevap.data || {};
          this.isler = (veri.isler || []) as Array<ExcelIsi>;
          if (veri.saklama_saat) this.saklamaSaat = veri.saklama_saat;
          this.listeHatasiGosterildi = false;
          // Sekme rozetinde hazır dosya sayısı görünsün.
          this.$emit("hazir-sayisi", this.hazirSayisi);
        })
        .catch((error: any) => {
          if (this.yokEdildi || axios.isCancel(error)) return;
          // Yanıtı olan hatada (ör. 500) genel hata kapısı (StatusCodeHelper) zaten
          // pencere gösterdi; ikinci mesaj verme. Kendi mesajımız yalnız yanıtsız
          // (ağ) hatada ve kesinti başına bir kez gösterilir.
          if (error && error.response) return;
          if (this.listeHatasiGosterildi) return;
          this.listeHatasiGosterildi = true;
          this.$toast.error("Excel çıktıları listelenemedi.");
        })
        .finally(() => {
          if (this.aktifListeIstegi === istek) this.aktifListeIstegi = null;
          if (this.yokEdildi) return;
          this.yukleniyor = false;
          this.tazelemeyiPlanla();
        });
  }

  /**
   * Yönetici görünümünde miyiz.
   *
   * Sunucu karar verir: "isteyen" alanını YALNIZ yöneticiye gönderiyor.
   * Ön yüzde ayrıca rol sorgulanmaz — yetki kararı tek yerde kalmalı,
   * yoksa iki taraf ayrışır ve ekran yetkisi olmayan bir kolon gösterir.
   */
  get yoneticiGorunumu(): boolean {
    return this.isler.some((i) => i.sahibi !== undefined);
  }

  /** Listede hazır görünen iş sayısı — sekme rozetinde kullanılır. */
  get hazirSayisi(): number {
    return this.isler.filter((i) => i.durum === "hazir").length;
  }

  /**
   * Kesilme notu.
   *
   * Sunucu sınırı göndermediyse SAYI UYDURULMAZ: bu değişiklikten önce üretilmiş
   * kayıtlar sınırı dosya sayısı olarak tutuyordu, onu satır diye göstermek
   * kullanıcıya yanlış bilgi verir. Sayı yoksa sayısız cümle kurulur.
   */
  kesilmeNotu(is: ExcelIsi): string {
    if (!is.satir_limiti) {
      return "Rapor tavanı aşıldı — rapor bundan büyük, dosya burada kesildi.";
    }

    return this.satirSayisi(is.satir_limiti) + " satır sınırına ulaşıldı — rapor bundan büyük, dosya burada kesildi.";
  }

  /** Satır sayısı da tutarlar gibi TR biçiminde okunsun (75.000, 75000 değil). */
  satirSayisi(deger?: number): string {
    return deger == null ? "—" : deger.toLocaleString("tr-TR");
  }

  durumEtiketi(durum: string): string {
    switch (durum) {
      case "kuyrukta": return "Sırada";
      case "hazirlaniyor": return "Hazırlanıyor";
      case "hazir": return "Hazır";
      case "hata": return "Üretilemedi";
      case "suresi_doldu": return "Süresi doldu";
      default: return durum;
    }
  }

  durumIkonu(durum: string): string {
    switch (durum) {
      case "kuyrukta": return "mdi-clock-outline";
      case "hazirlaniyor": return "mdi-loading mdi-spin";
      case "hazir": return "mdi-check-circle-outline";
      case "hata": return "mdi-alert-circle-outline";
      case "suresi_doldu": return "mdi-timer-off-outline";
      default: return "mdi-help-circle-outline";
    }
  }

  zaman(deger?: string): string {
    if (!deger) return "—";
    const t = new Date(deger);
    if (isNaN(t.getTime())) return String(deger);

    return t.toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  }

  indiriliyor(is: ExcelIsi): boolean {
    return !!this.indirilenIsler[is.is_key];
  }

  indir(is: ExcelIsi): Promise<void> {
    if (this.indiriliyor(is)) return Promise.resolve();
    this.$set(this.indirilenIsler, is.is_key, true);
    const istek = axios.CancelToken.source();
    this.aktifIndirmeIstekleri[is.is_key] = istek;

    return this.$http.get("/api/v1/rapor-ai/excel-indir/" + is.is_key, {
      responseType: "blob",
      cancelToken: istek.token,
      // [dev-3350] PR #1712 kardeş tarama — aşağıdaki catch "Dosya indirilemedi"
      // mesajını ZATEN veriyor; bastırma config'de yoksa 404/409'da ham genel
      // pencere de üstüne biniyor (çift geri bildirim). Yalnız bu istek için susturulur.
      sessizKodlar: [404, 409],
    })
        .then((blob: any) => {
          // Bazı özel adapter/mock'lar CancelToken'ı kendileri reddetmeyebilir;
          // yok edilmiş bir ekran yine de Blob indirmesini başlatmamalı.
          if (this.yokEdildi || istek.token.reason) return;
          const ad = (is.isim || "rapor") + ".xlsx";
          // İnen gövdeyi doğrudan kaydetme: sunucu hata döndürdüğünde gövde bir
          // JSON metni olabiliyor ve tarayıcı onu .xlsx adıyla kaydediyor —
          // kullanıcı hata mesajı değil, açılmayan bir dosya görüyor. Bu bir kez
          // yaşandı (14.09 test kaydı: 155 baytlık "Excel"). Yardımcı gövdenin
          // ilk iki baytına bakar; xlsx bir ZIP kabı olduğundan "PK" ile başlar.
          return sunucuExcelKaydet(blob, ad, (mesaj: string) => this.$toast.error(mesaj))
              .then((kaydedildi: boolean) => {
                // İmza doğrulaması hata gövdesi bulduysa helper zaten tek ve
                // anlaşılır toastı gösterdi. İkinci toast üretmeden durumu yenile.
                if (!kaydedildi) return this.yukle();
              });
        })
        .catch((error: any) => {
          if (this.yokEdildi || axios.isCancel(error)) return;
          // Dosya bu arada saklama süresini aşmış olabilir; liste tazelenince
          // durum "Süresi doldu"ya döner.
          this.$toast.error("Dosya indirilemedi. Listeyi yenileyip tekrar bakın.");
          this.yukle();
        })
        .finally(() => {
          if (this.aktifIndirmeIstekleri[is.is_key] === istek) {
            delete this.aktifIndirmeIstekleri[is.is_key];
          }
          if (!this.yokEdildi) this.$delete(this.indirilenIsler, is.is_key);
        });
  }
}
